import type { CSSProperties, MouseEvent } from 'react';
import { NodeChip } from '../../components/NodeChip';
import { DocPill } from '../../components/DocPill';
import {
  kindColorOf,
  PIPELINE_COLOR,
  PIPELINE_LABEL,
  REVISION_SOURCE_COLOR,
  asWorkflowState,
} from '../../lib/docs';
import type { DocOwner, DocRow, DocRowDoc, DocRowState } from '../../lib/docTable';
import type { DocType, NodeId, NodeMap } from '../../lib/types';

/**
 * The Documents tab's CELLS — one small component per column, and the four vocabularies
 * (kind colour, state colour, state label, initials) they share.
 *
 * They are here rather than inside `DocumentsTab.tsx` for one reason: the container is the
 * fetch, the filters and the actions, and a table row that renders seven columns inline buries
 * all three. Nothing here holds state or calls an action — every affordance is a callback the
 * container passes down, so the gates stay in ONE file and cannot be re-derived per cell.
 *
 * The colour tables are the app's own (`lib/docs.ts`): `DOC_BEAN_COLOR` for the kind, the
 * pipeline palette for the workflow state, `REVISION_SOURCE_COLOR` for a revision's source.
 * A row and the map pill for the same document are the same colour because they read the same
 * table, not because two lists happen to agree.
 */

export type LangList = readonly { code: string; name: string }[];

/** Open the locale popover for (row, locale), anchored on the element that was clicked. */
export type PickLocale = (
  anchor: HTMLElement,
  row: DocRowDoc,
  locale: string,
  focusResponsibles: boolean,
) => void;

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

export const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

/** Moved to `lib/docs.ts` (V0.46), where the search results reach it too. Re-exported so
 *  this module's existing importers keep their one import. */
export { kindColorOf };

export const stateColorOf = (state: DocRowState): string =>
  state === 'missing'
    ? 'var(--ink-faint)'
    : state === 'planned'
      ? 'var(--status-prompt-present)'
      : (PIPELINE_COLOR[state] ?? 'var(--ink-faint)');

export const stateLabelOf = (state: DocRowState): string =>
  state === 'missing' ? 'Not written' : state === 'planned' ? 'Planned' : (PIPELINE_LABEL[state] ?? state);

const cssVars = (vars: Record<string, string>) => vars as CSSProperties;

/* ------------------------------------------------------------------- Document ---------- */

/**
 * The document itself — THE APP'S PILL (`components/DocPill.tsx`, the nav-band size), the same
 * element the map rows, the node panel and the doc band draw for the same page, in the same
 * kind colour (V0.44.1; V0.44 had a pill of its own, which is the failure mode this codebase
 * has already paid for once).
 *
 * An OWED page is the dashed `planned` pill in its kind's colour, and for a viewer who may create
 * it, the pill IS the way to create it — exactly what the dotted pill on the map does: the page
 * is created, its editor opens over the dashboard, and closing it unwritten takes it back out.
 * The label already carries the "+"; the pill draws no icon (there is no page yet to have a
 * type). For a viewer who may not create it, the same pill is a label.
 */
export function DocumentCell({
  row,
  docTypes,
  aiVisible,
  onOpen,
  onCreate,
}: {
  row: DocRow;
  docTypes: Record<string, DocType>;
  aiVisible: boolean;
  onOpen: () => void;
  /** Create the owed page — absent when this viewer may not. Planned rows only. */
  onCreate?: () => void;
}) {
  if (row.kind === 'planned') {
    return (
      <>
        <DocPill
          variant="nav"
          planned
          label={row.title}
          color={kindColorOf(row.docKind)}
          className={onCreate ? 'dt-create' : 'dt-create static'}
          title={
            onCreate
              ? `${row.kindLabel} — planned here and not created yet. Click to create it.`
              : `${row.kindLabel} — planned here and not created yet. A branch responsible or a map editor can add it.`
          }
          onClick={onCreate}
          data={{ 'data-node': row.nodeId, 'data-kind': row.docKind }}
        />
        <span className="kind">{row.kindLabel} · owed by the plan</span>
      </>
    );
  }
  return (
    <>
      <DocPill
        variant="nav"
        label={row.title || '(untitled)'}
        color={kindColorOf(row.docKind)}
        docId={row.docPageId}
        docTypes={docTypes}
        className="dt-open-pill"
        onClick={onOpen}
      />
      {aiVisible && row.ai?.generated ? (
        <span className="aidoc-claude-badge" title="Generated by Claude">
          ✦ Claude
        </span>
      ) : null}
      <span className="kind">
        {row.kindLabel}
        {row.owners.length > 1 ? ` · on ${row.owners.length} nodes` : ''}
      </span>
    </>
  );
}

/* ----------------------------------------------------------------------- Node ---------- */

export function NodeCell({
  owners,
  nodes,
  isLight,
  onLocate,
}: {
  owners: readonly DocOwner[];
  nodes: NodeMap;
  isLight: boolean;
  onLocate: (id: NodeId) => void;
}) {
  if (!owners.length) {
    return (
      <>
        <span className="dt-muted">Not on the map</span>
        <span className="dt-crumb">unfiled page</span>
      </>
    );
  }
  return (
    <>
      {owners.map((o) => {
        const n = nodes[o.nodeId];
        return (
          <div className="dt-owner" key={o.nodeId}>
            <NodeChip
              node={{ root: n?.root, color: n?.color, label: o.label }}
              isLight={isLight}
              onClick={() => onLocate(o.nodeId)}
              title={`Show ${o.label} on the map`}
            />
            {o.breadcrumb.length ? <span className="dt-crumb">{o.breadcrumb.join(' › ')}</span> : null}
          </div>
        );
      })}
    </>
  );
}

/* ---------------------------------------------------------------------- State ---------- */

export function StateCell({
  row,
  currentLocale,
  sourceLocale,
}: {
  row: DocRow;
  currentLocale: string;
  sourceLocale: string;
}) {
  if (row.kind === 'planned') {
    return (
      <span className="dt-state muted" title="The plan names this document; nobody has created it yet">
        <span className="dt-dot dash" style={cssVars({ '--c': stateColorOf('planned') })} />
        Planned
      </span>
    );
  }
  if (row.state === 'missing') {
    return (
      <span className="dt-state muted" title={`Nothing written in ${currentLocale.toUpperCase()} yet`}>
        <span className="dt-dot dash" />
        Not written
        {row.fallback ? (
          <small>
            ({sourceLocale.toUpperCase()} shown: {stateLabelOf(asWorkflowState(row.fallback.workflowState))})
          </small>
        ) : null}
      </span>
    );
  }
  const c = row.current;
  const title = c?.reviewedByName
    ? `Last moved by ${c.reviewedByName}${c.reviewedAt ? ` on ${fmtDate(c.reviewedAt)}` : ''}`
    : 'Nobody has moved this state by hand';
  return (
    <span className="dt-state" title={title}>
      <span className="dt-dot" style={cssVars({ '--c': stateColorOf(row.state) })} />
      {stateLabelOf(row.state)}
    </span>
  );
}

/* ------------------------------------------------------------------ Languages ---------- */

export function LanguagesCell({
  row,
  languages,
  sourceLocale,
  currentLocale,
  onPick,
}: {
  row: DocRow;
  languages: LangList;
  sourceLocale: string;
  currentLocale: string;
  onPick: PickLocale;
}) {
  if (row.kind === 'planned') {
    return (
      <div className="dt-lchips">
        {languages.map((l) => (
          <span key={l.code} className="dt-lc none" title={`${l.name}: nothing written yet`}>
            {l.code.toUpperCase()}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="dt-lchips">
      {languages.map((l) => {
        const c = row.byLocale[l.code];
        const written = !!c?.written;
        const cls = [
          'dt-lc',
          written ? '' : 'none',
          c?.stale ? 'stale' : '',
          l.code === currentLocale ? 'cur' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const title = written
          ? `${l.name}: ${stateLabelOf(asWorkflowState(c!.workflowState))}` +
            (c!.stale
              ? ` · ${c!.behind} version${c!.behind === 1 ? '' : 's'} behind ${sourceLocale.toUpperCase()}`
              : '')
          : `${l.name}: not written yet`;
        return (
          <button
            key={l.code}
            type="button"
            className={cls}
            data-loc={l.code}
            data-doc={row.docPageId}
            title={title}
            style={cssVars({ '--c': written ? stateColorOf(asWorkflowState(c!.workflowState)) : 'var(--ink-faint)' })}
            onClick={(e: MouseEvent<HTMLButtonElement>) => onPick(e.currentTarget, row, l.code, false)}
          >
            {l.code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- Responsible ---------- */

function Avatars({
  users,
  groups,
  nameOf,
  groupNameOf,
}: {
  users: readonly string[];
  groups?: readonly string[];
  nameOf: (id: string) => string;
  groupNameOf?: (id: string) => string;
}) {
  return (
    <span className="wf-resp-stack">
      {users.slice(0, 3).map((id) => (
        <span className="wf-resp-av" key={id} title={nameOf(id)}>
          {initialsOf(nameOf(id))}
        </span>
      ))}
      {(groups ?? []).slice(0, 2).map((id) => (
        <span className="wf-resp-av grp" key={id} title={groupNameOf ? groupNameOf(id) : id}>
          {initialsOf(groupNameOf ? groupNameOf(id) : id)}
        </span>
      ))}
    </span>
  );
}

export function ResponsibleCell({
  row,
  currentLocale,
  nameOf,
  groupNameOf,
  onPick,
}: {
  row: DocRow;
  currentLocale: string;
  nameOf: (id: string) => string;
  groupNameOf: (id: string) => string;
  onPick: PickLocale;
}) {
  if (row.kind === 'planned') {
    const ids = row.responsibles.nodeUsers;
    return ids.length ? (
      <span className="dt-resp inherit static" title="Owed by this node — its responsibles are who would be asked">
        <Avatars users={ids} nameOf={nameOf} />
        <span className="n">via node</span>
      </span>
    ) : (
      <span className="dt-resp none static">
        <span className="n">Nobody yet</span>
      </span>
    );
  }

  const { docUsers, docGroups, nodeUsers } = row.responsibles;
  const n = docUsers.length + docGroups.length;
  const open = (e: MouseEvent<HTMLButtonElement>) => onPick(e.currentTarget, row, currentLocale, true);
  const lang = currentLocale.toUpperCase();

  if (n) {
    const only = docUsers.length ? nameOf(docUsers[0]) : groupNameOf(docGroups[0]);
    return (
      <button
        type="button"
        className="dt-resp"
        title={`Responsible for the ${lang} version`}
        onClick={open}
      >
        <Avatars users={docUsers} groups={docGroups} nameOf={nameOf} groupNameOf={groupNameOf} />
        <span className="n">{n === 1 ? only : n}</span>
        <span className="dt-caret" aria-hidden="true">
          ▾
        </span>
      </button>
    );
  }
  if (nodeUsers.length) {
    return (
      <button
        type="button"
        className="dt-resp inherit"
        title={`Nobody is assigned to this page in ${lang} — these people own the node it sits on`}
        onClick={open}
      >
        <Avatars users={nodeUsers} nameOf={nameOf} />
        <span className="n">via node</span>
        <span className="dt-caret" aria-hidden="true">
          ▾
        </span>
      </button>
    );
  }
  return (
    <button type="button" className="dt-resp none" title={`Nobody owns the ${lang} version`} onClick={open}>
      <span className="n">Unassigned</span>
      <span className="dt-caret" aria-hidden="true">
        ▾
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------- Last change ---------- */

export function LastChangeCell({ row, aiVisible }: { row: DocRow; aiVisible: boolean }) {
  const c = row.kind === 'doc' ? row.current : null;
  if (!c || !c.lastChangeAt) return <span className="tgt-meta">—</span>;
  const source = aiVisible ? c.ai?.lastRun?.source : undefined;
  return (
    <>
      <span className="dt-when">
        <span
          className="dt-kdot"
          style={cssVars({ '--c': c.lastChangeKind === 'ai' ? 'var(--accent)' : 'var(--ink-faint)' })}
        />
        {fmtDate(c.lastChangeAt)}
      </span>
      <span className="who">{c.lastChangeBy ?? ''}</span>
      {source ? (
        <span className="who" style={{ color: REVISION_SOURCE_COLOR[source] ?? 'var(--ink-faint)' }}>
          {source}
        </span>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------------- AI ---------- */

export function AiCell({
  row,
  languages,
  busy,
  showRegen,
  showTranslate,
  onRegen,
  onTranslate,
  onConfigure,
}: {
  row: DocRowDoc;
  languages: LangList;
  busy?: { cls: string; text: string };
  showRegen: boolean;
  showTranslate: boolean;
  onRegen: () => void;
  onTranslate: () => void;
  /** "Configure": hand the page's primary node to the AI generation tab (V0.44.1). */
  onConfigure?: () => void;
}) {
  const runs = languages
    .map((l) => row.byLocale[l.code]?.ai?.lastRun)
    .filter((r): r is NonNullable<typeof r> => !!r)
    .sort((a, b) => (a.at < b.at ? 1 : -1));
  const last = runs[0];
  const guard = row.ai?.guard ?? null;
  const live = row.ai?.lastLiveRun ?? null;

  return (
    <div className="dt-aicell">
      {last ? (
        <span>
          <span
            className="dt-kdot"
            style={cssVars({ '--c': REVISION_SOURCE_COLOR[last.source] ?? 'var(--accent)' })}
          />
          {fmtDate(last.at)} · {last.source === 'ai_translated' ? 'translated' : 'generated'}
          {last.model ? <span className="tgt-meta"> · {last.model}</span> : null}
        </span>
      ) : (
        <span className="tgt-meta">No AI run yet</span>
      )}

      <span className="dt-emb" title="Which languages the chatbot can answer from">
        {languages.map((l) => {
          const c = row.byLocale[l.code];
          return (
            <span key={l.code} className={c?.written && c.ai?.embedded ? 'on' : ''}>
              {l.code.toUpperCase()}
            </span>
          );
        })}
        <span className="tgt-meta">indexed</span>
      </span>

      {guard ? (
        <span
          className="dt-badge warn"
          title={`Last by ${guard.lastBy} on ${fmtDate(guard.lastAt)} — regenerating would overwrite them`}
        >
          ⚠ {guard.count} human edit{guard.count === 1 ? '' : 's'} since generation
        </span>
      ) : null}

      {live ? (
        <span
          className="dt-badge ok"
          title={
            `Live verification: ${live.state}` +
            (row.ai?.liveObservationAt ? ` · observed ${fmtDate(row.ai.liveObservationAt)}` : '')
          }
        >
          ✓ verified live {fmtDate(live.at)}
        </span>
      ) : null}

      {showRegen || showTranslate || busy || onConfigure ? (
        <span className="dt-acts">
          {onConfigure ? (
            <button
              type="button"
              className="aidoc-actbtn dt-configure"
              title="This node's AI prompt and generation, in the AI generation tab"
              onClick={onConfigure}
            >
              Configure
            </button>
          ) : null}
          {showRegen ? (
            <button
              type="button"
              className="aidoc-actbtn regen dt-regen"
              disabled={busy?.cls === 'generating'}
              onClick={onRegen}
            >
              Regenerate
            </button>
          ) : null}
          {showTranslate ? (
            <button
              type="button"
              className="aidoc-actbtn dt-translate"
              disabled={busy?.cls === 'generating'}
              onClick={onTranslate}
            >
              Translate
            </button>
          ) : null}
          {busy ? <span className={`rowstate ${busy.cls}`}>{busy.text}</span> : null}
        </span>
      ) : null}
    </div>
  );
}
